import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { SEO } from "../components/SEO";
import {
  Trophy, Users, Building2, Heart, Music, GraduationCap,
  PartyPopper, Paintbrush, ArrowRight, Shirt
} from "lucide-react";

const TEMPLATES = [
  {
    category: "Sports",
    icon: Trophy,
    color: "blue",
    items: [
      { id: "sports-jersey", name: "Team Jersey", desc: "Player name & number", garment: "tshirt", method: "screen_printing", color: "Navy", placement: "front", text: "TEAM NAME" },
      { id: "sports-coach", name: "Coach Polo", desc: "Embroidered team logo", garment: "polo", method: "embroidery", color: "Black", placement: "left_chest", text: "COACH" },
      { id: "sports-fan", name: "Fan Hoodie", desc: "Team spirit wear", garment: "hoodie", method: "dtf_printing", color: "Red", placement: "front", text: "GO TEAM" },
    ],
  },
  {
    category: "Corporate",
    icon: Building2,
    color: "gray",
    items: [
      { id: "corp-polo", name: "Staff Polo", desc: "Logo on chest", garment: "polo", method: "embroidery", color: "White", placement: "left_chest", text: "COMPANY" },
      { id: "corp-hoodie", name: "Company Hoodie", desc: "Back print logo", garment: "hoodie", method: "screen_printing", color: "Charcoal", placement: "front", text: "EST. 2024" },
      { id: "corp-event", name: "Event Tee", desc: "Full color front print", garment: "tshirt", method: "dtf_printing", color: "White", placement: "front", text: "ANNUAL RETREAT" },
    ],
  },
  {
    category: "Events",
    icon: PartyPopper,
    color: "orange",
    items: [
      { id: "event-5k", name: "5K Race Shirt", desc: "Run date & logo", garment: "tshirt", method: "screen_printing", color: "Orange", placement: "front", text: "5K FUN RUN" },
      { id: "event-concert", name: "Concert Merch", desc: "Band artwork", garment: "tshirt", method: "dtf_printing", color: "Black", placement: "front", text: "LIVE TOUR 2026" },
      { id: "event-gala", name: "Gala Crew", desc: "Staff identification", garment: "polo", method: "embroidery", color: "Black", placement: "left_chest", text: "STAFF" },
    ],
  },
  {
    category: "Fundraiser",
    icon: Heart,
    color: "red",
    items: [
      { id: "fund-walk", name: "Charity Walk Tee", desc: "Cause branding", garment: "tshirt", method: "screen_printing", color: "Pink", placement: "front", text: "WALK FOR HOPE" },
      { id: "fund-school", name: "School Spirit", desc: "Mascot & year", garment: "tshirt", method: "dtf_printing", color: "Navy", placement: "front", text: "CLASS OF 2026" },
      { id: "fund-church", name: "Church Group", desc: "Group identity", garment: "tshirt", method: "screen_printing", color: "Purple", placement: "front", text: "FAITH COMMUNITY" },
    ],
  },
  {
    category: "Music & Creative",
    icon: Music,
    color: "purple",
    items: [
      { id: "music-band", name: "Band Tee", desc: "Tour merch style", garment: "tshirt", method: "dtf_printing", color: "Black", placement: "front", text: "BAND NAME" },
      { id: "music-crew", name: "Crew Hoodie", desc: "Backstage crew", garment: "hoodie", method: "screen_printing", color: "Black", placement: "back", text: "CREW" },
      { id: "music-dj", name: "DJ Cap", desc: "Embroidered logo", garment: "hat", method: "embroidery", color: "Black", placement: "front", text: "DJ" },
    ],
  },
  {
    category: "School",
    icon: GraduationCap,
    color: "green",
    items: [
      { id: "school-grad", name: "Graduation Tee", desc: "Class year & school", garment: "tshirt", method: "screen_printing", color: "Navy", placement: "front", text: "GRADUATES 2026" },
      { id: "school-club", name: "Club Shirt", desc: "Club logo & members", garment: "tshirt", method: "dtf_printing", color: "Forest", placement: "front", text: "SCIENCE CLUB" },
      { id: "school-staff", name: "Teacher Polo", desc: "School emblem", garment: "polo", method: "embroidery", color: "Navy", placement: "left_chest", text: "STAFF" },
    ],
  },
];

const COLORS_HEX = {
  White: "#FFFFFF", Black: "#1a1a1a", Navy: "#1e3a5f", Red: "#c0392b",
  Orange: "#e67e22", Charcoal: "#444444", Pink: "#e91e8a",
  Purple: "#6c3483", Forest: "#27ae60",
};

const SHAPES = {
  tshirt: "M 25,18 Q 22,18 15,22 L 8,35 L 18,38 L 22,28 L 22,78 Q 22,82 28,82 L 72,82 Q 78,82 78,78 L 78,28 L 82,38 L 92,35 L 85,22 Q 78,18 75,18 Q 70,18 62,16 Q 56,14 50,14 Q 44,14 38,16 Q 30,18 25,18 Z",
  hoodie: "M 22,20 Q 18,20 10,24 L 2,40 L 14,44 L 18,30 L 18,82 Q 18,86 24,86 L 76,86 Q 82,86 82,82 L 82,30 L 86,44 L 98,40 L 90,24 Q 82,20 78,20 Q 72,20 64,18 L 62,12 Q 56,8 50,8 Q 44,8 38,12 L 36,18 Q 28,20 22,20 Z",
  polo: "M 25,18 Q 22,18 15,22 L 8,35 L 18,38 L 22,28 L 22,78 Q 22,82 28,82 L 72,82 Q 78,82 78,78 L 78,28 L 82,38 L 92,35 L 85,22 Q 78,18 75,18 Q 70,18 62,16 Q 56,14 50,14 Q 44,14 38,16 Q 30,18 25,18 Z",
  hat: "M 15,55 Q 15,35 30,25 Q 40,18 50,18 Q 60,18 70,25 Q 85,35 85,55 L 85,60 Q 50,62 15,60 Z",
};

const TemplateLibrary = () => {
  const navigate = useNavigate();

  const useTemplate = (template) => {
    const params = new URLSearchParams({
      garment: template.garment,
      method: template.method,
      cat: "apparel",
    });
    navigate(`/design-studio?${params.toString()}`);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="template-library">
      <SEO title="Design Templates" description="Pre-designed templates for custom printing" path="/templates" />

      <div className="text-center mb-8">
        <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted">Start Here</span>
        <h1 className="font-archivo text-4xl sm:text-5xl uppercase tracking-tight text-ink mb-2">Design Templates</h1>
        <p className="font-inter text-sm text-muted">Pick a template, customize it in the Design Studio, and order. It's that easy.</p>
      </div>

      {TEMPLATES.map(cat => (
        <div key={cat.category} className="mb-8" data-testid={`template-cat-${cat.category.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="flex items-center gap-2 mb-3">
            <cat.icon className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-bold text-gray-800">{cat.category}</h2>
            <Badge variant="secondary" className="text-[10px]">{cat.items.length} templates</Badge>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {cat.items.map(t => {
              const hex = COLORS_HEX[t.color] || "#FFFFFF";
              const isDark = ["Black", "Navy", "Charcoal", "Purple", "Forest"].includes(t.color);
              const shape = SHAPES[t.garment] || SHAPES.tshirt;

              return (
                <Card key={t.id} className="border-0 shadow-sm hover:shadow-md transition group overflow-hidden" data-testid={`template-${t.id}`}>
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-3" style={{ height: 140 }}>
                    <svg viewBox="0 0 100 100" className="h-full" style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.08))" }}>
                      <path d={shape} fill={hex} stroke={isDark ? "#555" : "#ddd"} strokeWidth="0.5" />
                      <text x="50" y="48" textAnchor="middle" fill={isDark ? "#fff" : "#333"} fontSize="5" fontWeight="bold" opacity="0.5">
                        {t.text}
                      </text>
                    </svg>
                  </div>
                  <CardContent className="p-3">
                    <h3 className="text-sm font-bold text-gray-900">{t.name}</h3>
                    <p className="text-xs text-gray-500 mb-2">{t.desc}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: hex }} />
                        <span className="text-[10px] text-gray-400">{t.color} &middot; {t.method.replace(/_/g, " ")}</span>
                      </div>
                      <Button size="sm" onClick={() => useTemplate(t)} className="h-7 text-[10px] bg-orange-600 hover:bg-orange-700 text-white opacity-0 group-hover:opacity-100 transition" data-testid={`use-${t.id}`}>
                        Customize <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ))}

      {/* CTA */}
      <Card className="border-0 shadow-md bg-gradient-to-r from-gray-900 to-gray-800 text-white mt-8">
        <CardContent className="p-6 text-center">
          <Paintbrush className="w-8 h-8 text-orange-400 mx-auto mb-2" />
          <h3 className="text-lg font-bold mb-1">Don't see what you need?</h3>
          <p className="text-sm text-gray-400 mb-3">Start from scratch in our Design Studio or let our team create something custom.</p>
          <div className="flex justify-center gap-3">
            <Button onClick={() => navigate("/design-studio")} className="bg-gradient-to-r from-orange-500 to-red-600 text-white">
              <Shirt className="w-4 h-4 mr-1" /> Start Designing
            </Button>
            <Button onClick={() => navigate("/design-services")} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700">
              Request Custom Design
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TemplateLibrary;
